import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { vacancyService } from '../../services/vacancyService';
import { authService } from '../../services/authService';
import { toast } from 'react-toastify';
import CompanyNav from '../../components/CompanyNav';
import CompanyBottomNavigation from '../../components/CompanyBottomNavigation';
import api from '../../config/api';
import Loader from '../../components/Loader';

const EditVacancy = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const user = authService.getCurrentUser();
  const [submitting, setSubmitting] = useState(false);
  const [jobTitles, setJobTitles] = useState([]);
  const [loading, setLoading] = useState(true);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    loadJobTitles();
    loadVacancy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadJobTitles = async () => {
    try {
      const response = await api.get('/job-titles');
      if (Array.isArray(response.data)) {
        setJobTitles(response.data);
      } else if (response.data?.data) {
        setJobTitles(response.data.data);
      }
    } catch (error) {
      console.error('Error loading job titles:', error);
    }
  };

  const loadVacancy = async () => {
    try {
      setLoading(true);
      const res = await vacancyService.getCompanyVacancyById(id);
      const data = res?.data || res?.success && res?.data ? res.data : res;
      if (!data) {
        toast.error('تعذر تحميل بيانات الوظيفة');
        navigate('/company/vacancies');
        return;
      }
      // Map fields to form
      setValue('job_title_id', data?.job_title?.id || data?.job_title_id || '');
      setValue('type_of_work', data?.type_of_work || '');
      setValue('presence_type', data?.presence_type || '');
      setValue('experience', data?.experience || '');
      setValue('academic_degree', data?.academic_degree || '');
      setValue('salary', data?.salary || '');
      setValue('currency', data?.currency || '');
      setValue('location_lat', data?.location?.lat || '');
      setValue('location_long', data?.location?.long || '');
      setValue('description', data?.description || '');
      setValue('privileges', data?.privileges || '');
      setValue('status', data?.status || 'open');
    } catch (error) {
      const message = error?.response?.data?.message || 'تعذر تحميل بيانات الوظيفة';
      toast.error(message);
      navigate('/company/vacancies');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      setSubmitting(true);
      if (data.location_lat && data.location_long) {
        data.location = {
          lat: parseFloat(data.location_lat),
          long: parseFloat(data.location_long),
        };
        delete data.location_lat;
        delete data.location_long;
      }
      const response = await vacancyService.update(id, data);
      if (response.success) {
        toast.success('تم تحديث الوظيفة بنجاح!');
        navigate('/company/vacancies');
      } else {
        toast.success('تم تحديث الوظيفة');
        navigate('/company/vacancies');
      }
    } catch (error) {
      const message = error.response?.data?.message || 'فشل تحديث الوظيفة';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loader fullScreen={true} size="large" />;
  }

  return (
    <div className="employee-home-page" dir="rtl">
      <CompanyNav user={user} />

      {/* Header Section */}
      <div className="px-3 mb-4 mt-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h3 className="section-title mb-1">تعديل الوظيفة</h3>
            <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>حدث تفاصيل الوظيفة</p>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="apply-form px-3 mb-5">
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Basic Information */}
          <div className="form-section-apply mb-4">
            <h3 className="form-section-title">المعلومات الأساسية</h3>

            <div className="edit-profile-field-container">
              <label className="edit-profile-label" htmlFor="job_title_id">
                المسمى الوظيفي <span style={{ color: '#608fd3' }}>*</span>
              </label>
              <select
                id="job_title_id"
                className="edit-profile-input"
                {...register('job_title_id', {
                  required: 'المسمى الوظيفي مطلوب',
                })}
              >
                <option value="">اختر المسمى الوظيفي</option>
                {jobTitles.map((jobTitle) => (
                  <option key={jobTitle.id} value={jobTitle.id}>
                    {jobTitle.name}
                  </option>
                ))}
              </select>
              {errors.job_title_id && (
                <span className="edit-profile-error">{errors.job_title_id.message}</span>
              )}
            </div>

            <div className="edit-profile-field-container">
              <label className="edit-profile-label" htmlFor="type_of_work">نوع العمل</label>
              <select id="type_of_work" className="edit-profile-input" {...register('type_of_work')}>
                <option value="">اختر النوع</option>
                <option value="full_time">دوام كامل</option>
                <option value="part_time">دوام جزئي</option>
                <option value="contract">عقد</option>
                <option value="freelance">عمل حر</option>
              </select>
            </div>

            <div className="edit-profile-field-container">
              <label className="edit-profile-label" htmlFor="presence_type">نوع الحضور</label>
              <select id="presence_type" className="edit-profile-input" {...register('presence_type')}>
                <option value="">اختر النوع</option>
                <option value="remote">عن بعد</option>
                <option value="hybrid">مختلط</option>
                <option value="onsite">على موقع</option>
              </select>
            </div>

            <div className="d-flex gap-2">
              <div className="edit-profile-field-container flex-grow-1">
                <label className="edit-profile-label" htmlFor="experience">سنوات الخبرة</label>
                <input
                  type="number"
                  id="experience"
                  min="0"
                  className="edit-profile-input"
                  {...register('experience')}
                />
              </div>

              <div className="edit-profile-field-container flex-grow-1">
                <label className="edit-profile-label" htmlFor="academic_degree">الدرجة العلمية</label>
                <input
                  type="text"
                  id="academic_degree"
                  className="edit-profile-input"
                  placeholder="مثال: بكالوريوس"
                  {...register('academic_degree')}
                />
              </div>
            </div>
          </div>

          {/* Salary & Location */}
          <div className="form-section-apply mb-4">
            <h3 className="form-section-title">الراتب والموقع</h3>

            <div className="d-flex gap-2">
              <div className="edit-profile-field-container flex-grow-1">
                <label className="edit-profile-label" htmlFor="salary">الراتب</label>
                <input
                  type="number"
                  id="salary"
                  step="0.01"
                  min="0"
                  className="edit-profile-input"
                  {...register('salary')}
                />
              </div>

              <div className="edit-profile-field-container flex-grow-1">
                <label className="edit-profile-label" htmlFor="currency">العملة</label>
                <input
                  type="text"
                  id="currency"
                  placeholder="ج.م"
                  className="edit-profile-input"
                  {...register('currency')}
                />
              </div>
            </div>

            <div className="d-flex gap-2">
              <div className="edit-profile-field-container flex-grow-1">
                <label className="edit-profile-label" htmlFor="location_lat">خط العرض</label>
                <input
                  type="number"
                  id="location_lat"
                  step="0.000001"
                  className="edit-profile-input"
                  {...register('location_lat')}
                />
              </div>

              <div className="edit-profile-field-container flex-grow-1">
                <label className="edit-profile-label" htmlFor="location_long">خط الطول</label>
                <input
                  type="number"
                  id="location_long"
                  step="0.000001"
                  className="edit-profile-input"
                  {...register('location_long')}
                />
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="form-section-apply mb-4">
            <h3 className="form-section-title">التفاصيل</h3>

            <div className="edit-profile-field-container">
              <label className="edit-profile-label" htmlFor="description">الوصف</label>
              <textarea
                id="description"
                rows="6"
                className="notes-textarea form-control"
                placeholder="اكتب وصفاً مفصلاً عن الوظيفة..."
                {...register('description')}
              />
            </div>

            <div className="edit-profile-field-container">
              <label className="edit-profile-label" htmlFor="privileges">المميزات والفوائد</label>
              <textarea
                id="privileges"
                rows="6"
                className="notes-textarea form-control"
                placeholder="اكتب المميزات والفوائد المقدمة..."
                {...register('privileges')}
              />
            </div>

            <div className="edit-profile-field-container">
              <label className="edit-profile-label" htmlFor="status">الحالة</label>
              <select id="status" className="edit-profile-input" {...register('status')}>
                <option value="open">مفتوحة</option>
                <option value="closed">مغلقة</option>
              </select>
            </div>
          </div>

          {/* Form Actions */}
          <div className="d-flex gap-2 mb-5" style={{ paddingBottom: '100px' }}>
            <button
              type="button"
              onClick={() => navigate('/company/vacancies')}
              className="btn-view-vacancy"
              style={{ flex: 1 }}
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="btn-apply"
              style={{
                flex: 1,
                background: 'linear-gradient(135deg, #608fd3 0%, #4a7bc4 100%)',
                color: 'white',
                padding: '0.875rem 1.5rem',
                borderRadius: '0.75rem',
                fontSize: '1rem',
                fontWeight: '600',
                border: 'none'
              }}
            >
              {submitting ? 'جاري الحفظ...' : 'حفظ التغييرات'}
            </button>
          </div>
        </form>
      </div>

      <CompanyBottomNavigation />
    </div>
  );
};

export default EditVacancy;


